import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Dumbbell, Trophy, Users, Zap, Clock, Calendar, Camera, TrendingUp,
  Play, Check, ShieldCheck, Star, Award, ArrowRight, Sparkles, Coins,
  Heart, ChevronDown, Lock, Timer, Video, MessageCircle, Gift,
} from 'lucide-react';

const gold = '#D4AF37';
const API = process.env.REACT_APP_BACKEND_URL;

const AnimatedCounter = ({ end, duration = 1400, suffix = '' }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = end / (duration / 20);
    const t = setInterval(() => {
      start += step;
      if (start >= end) { setN(end); clearInterval(t); }
      else setN(Math.floor(start));
    }, 20);
    return () => clearInterval(t);
  }, [end, duration]);
  return <>{n.toLocaleString('pt-BR')}{suffix}</>;
};

const Section = ({ children, className = '', id }) => (
  <section id={id} className={`px-6 md:px-10 py-16 md:py-28 max-w-6xl mx-auto ${className}`}>
    {children}
  </section>
);

const SectionKicker = ({ children }) => (
  <p className="text-[11px] uppercase tracking-[0.35em] font-bold mb-3" style={{ color: gold }}>{children}</p>
);

const H2 = ({ children }) => (
  <h2 className="font-primary font-black text-3xl md:text-5xl text-white leading-[1.05] mb-6">{children}</h2>
);

const CTAButton = ({ children, className = '', ...props }) => (
  <a
    {...props}
    className={`group relative inline-flex items-center justify-center gap-2 px-8 py-4 md:px-10 md:py-5 rounded-full font-black text-sm md:text-base uppercase tracking-wider text-black bg-gradient-to-b from-[#F5E28A] via-[#D4AF37] to-[#A8791D] shadow-[0_10px_40px_rgba(212,175,55,0.35)] hover:shadow-[0_14px_50px_rgba(212,175,55,0.55)] transition-all active:scale-[0.97] ${className}`}
  >
    <span>{children}</span>
    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
  </a>
);

const TrustPill = ({ icon: Icon, text }) => (
  <div className="flex items-center gap-2 px-3 py-2 rounded-full bg-white/5 border border-white/10 text-xs md:text-sm text-zinc-300">
    <Icon className="w-3.5 h-3.5" style={{ color: gold }} />
    {text}
  </div>
);

const useCountdownToMidnight = (enabled) => {
  const [remaining, setRemaining] = useState({ h: 0, m: 0, s: 0 });
  useEffect(() => {
    if (!enabled) return;
    const tick = () => {
      const now = new Date();
      const end = new Date(now);
      end.setHours(23, 59, 59, 999);
      const diff = Math.max(0, end - now);
      setRemaining({
        h: Math.floor(diff / 3600000),
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [enabled]);
  return remaining;
};

const pad = (n) => String(n).padStart(2, '0');

const ScarcityBar = ({ scarcity, variant = 'default' }) => {
  const cd = useCountdownToMidnight(scarcity?.enabled && scarcity?.show_countdown);
  if (!scarcity?.enabled) return null;
  const isCompact = variant === 'compact';
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`inline-flex flex-wrap items-center gap-2 md:gap-3 rounded-full border ${isCompact ? 'px-3 py-1.5' : 'px-4 py-2.5'}`}
      style={{ borderColor: 'rgba(239,68,68,0.4)', background: 'linear-gradient(90deg, rgba(239,68,68,0.12), rgba(212,175,55,0.08))' }}
      data-testid="scarcity-bar"
    >
      <span className="relative flex items-center justify-center w-2.5 h-2.5">
        <span className="absolute inset-0 rounded-full bg-red-500 opacity-70 animate-ping" />
        <span className="relative w-2.5 h-2.5 rounded-full bg-red-500" />
      </span>
      <span className={`font-black uppercase tracking-wider text-white ${isCompact ? 'text-[10px]' : 'text-xs md:text-sm'}`}>
        Últimas <span className="text-red-400">{scarcity.spots}</span> vagas
      </span>
      {scarcity.show_countdown && (
        <>
          <span className="w-px h-3 bg-white/20" />
          <span className={`text-zinc-300 ${isCompact ? 'text-[10px]' : 'text-xs md:text-sm'}`}>
            Oferta expira em{' '}
            <span className="font-mono font-black" style={{ color: gold }}>
              {pad(cd.h)}:{pad(cd.m)}:{pad(cd.s)}
            </span>
          </span>
        </>
      )}
    </motion.div>
  );
};

const FeatureCard = ({ icon: Icon, title, desc }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: '-50px' }}
    transition={{ duration: 0.5 }}
    className="group relative rounded-2xl p-6 bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/10 hover:border-[#D4AF37]/40 transition-all overflow-hidden"
  >
    <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-[#D4AF37]/5 blur-2xl group-hover:bg-[#D4AF37]/15 transition-all" />
    <div className="relative">
      <div className="w-12 h-12 rounded-xl bg-white/5 border border-white/10 flex items-center justify-center mb-4">
        <Icon className="w-5 h-5" style={{ color: gold }} />
      </div>
      <h3 className="font-bold text-white text-lg mb-2">{title}</h3>
      <p className="text-zinc-400 text-sm leading-relaxed">{desc}</p>
    </div>
  </motion.div>
);

const FAQItem = ({ q, a, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10 py-5">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 text-left"
        data-testid={testId}
      >
        <span className="text-white font-bold text-base md:text-lg">{q}</span>
        <ChevronDown className={`w-5 h-5 text-zinc-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} style={{ color: open ? gold : undefined }} />
      </button>
      {open && <p className="mt-3 text-zinc-400 text-sm md:text-base leading-relaxed">{a}</p>}
    </div>
  );
};

export const LandingPage = () => {
  const [videoUrl, setVideoUrl] = useState('');
  const [images, setImages] = useState({ hero_video_bg: '', thiago_photo: '', community_photo: '' });
  const [gallery, setGallery] = useState([]);
  const [scarcity, setScarcity] = useState({ enabled: false, spots: 10, show_countdown: true });
  useEffect(() => {
    document.title = 'Ferraz Team | Consultoria Fitness com Plataforma Exclusiva';
    fetch(`${API}/api/settings/landing-video`).then((r) => r.json()).then((d) => setVideoUrl(d?.url || '')).catch(() => {});
    fetch(`${API}/api/settings/landing-images`).then((r) => r.json()).then((d) => setImages({
      hero_video_bg: d?.hero_video_bg || '',
      thiago_photo: d?.thiago_photo || '',
      community_photo: d?.community_photo || '',
    })).catch(() => {});
    fetch(`${API}/api/settings/landing-gallery`).then((r) => r.json()).then((d) => setGallery(d?.photos || [])).catch(() => {});
    fetch(`${API}/api/settings/landing-scarcity`).then((r) => r.json()).then((d) => setScarcity({
      enabled: !!d?.enabled,
      spots: Number(d?.spots) || 10,
      show_countdown: d?.show_countdown !== false,
    })).catch(() => {});
  }, []);

  const resolveImg = (val, fallback) => {
    if (!val) return fallback;
    if (val.startsWith('http')) return val;
    return `${API}${val}`;
  };
  const heroBgUrl = resolveImg(images.hero_video_bg, 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800&auto=format&fit=crop&q=60');
  const thiagoUrl = resolveImg(images.thiago_photo, 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=600&auto=format&fit=crop&q=60');
  const communityUrl = resolveImg(images.community_photo, 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=800&auto=format&fit=crop&q=60');

  const embedUrl = (() => {
    if (!videoUrl) return '';
    const patterns = [/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/shorts\/)([\w-]{11})/];
    for (const p of patterns) {
      const m = videoUrl.match(p);
      if (m) return `https://www.youtube.com/embed/${m[1]}?autoplay=0&rel=0`;
    }
    return videoUrl;
  })();

  const CTA_HREF = 'https://chk.eduzz.com/2380868';
  const ctaProps = { target: '_blank', rel: 'noopener noreferrer' };

  return (
    <div className="bg-black text-white overflow-x-hidden font-secondary">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${gold}, #8B6914)` }}>
              <Dumbbell className="w-4 h-4 text-black" />
            </div>
            <span className="font-primary font-black text-lg tracking-wider">FERRAZ<span style={{ color: gold }}>.</span>TEAM</span>
          </div>
          <div className="flex items-center gap-3">
            <a href={CTA_HREF} {...ctaProps} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider text-black" style={{ background: gold }}>
              Começar
            </a>
          </div>
        </div>
      </header>

      {/* HERO */}
      <Section className="!pt-32 md:!pt-40 !pb-16">
        <div className="grid md:grid-cols-2 gap-10 md:gap-16 items-center">
          <div>
            <SectionKicker>Consultoria online de alta performance</SectionKicker>
            <h1 className="font-primary font-black text-4xl md:text-6xl leading-[1.02] mb-6">
              Pare de treinar<br />
              <span style={{ color: gold }}>sem resultado.</span>
            </h1>
            <p className="text-zinc-400 text-base md:text-lg leading-relaxed mb-8 max-w-xl">
              Plano personalizado, acompanhamento profissional e uma plataforma que vai te manter motivado <span className="text-white font-bold">todos os dias</span>. A Ferraz Team é muito mais que consultoria — é evolução completa.
            </p>
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <CTAButton href={CTA_HREF} {...ctaProps} data-testid="cta-hero">Quero começar agora</CTAButton>
            </div>
            {scarcity.enabled && (
              <div className="mb-8">
                <ScarcityBar scarcity={scarcity} />
              </div>
            )}
            <div className="flex flex-wrap gap-2">
              <TrustPill icon={ShieldCheck} text="Compra 100% segura" />
              <TrustPill icon={Check} text="Garantia de 7 dias" />
              <TrustPill icon={Star} text="Plataforma exclusiva" />
              <TrustPill icon={Users} text="Comunidade" />
            </div>
          </div>

          {/* Video (uses admin-configured URL when available) */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            className="relative aspect-video rounded-2xl overflow-hidden border border-white/10 shadow-[0_30px_100px_rgba(212,175,55,0.15)] bg-black"
          >
            {embedUrl ? (
              <iframe
                src={embedUrl}
                title="Ferraz Team - Vídeo de vendas"
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <>
                <div className="absolute inset-0 bg-gradient-to-br from-black via-zinc-900 to-black" />
                <div className="absolute inset-0 bg-cover bg-center opacity-40" style={{ backgroundImage: `url('${heroBgUrl}')` }} />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center shadow-2xl" style={{ background: gold }}>
                    <Play className="w-8 h-8 text-black fill-black ml-1" />
                  </div>
                </div>
                <div className="absolute bottom-4 left-4 right-4">
                  <span className="text-xs uppercase tracking-widest font-bold text-white/70">Vídeo em breve</span>
                </div>
              </>
            )}
          </motion.div>
        </div>
      </Section>

      {/* SOCIAL PROOF - Numbers */}
      <div className="border-y border-white/10 bg-gradient-to-b from-black to-zinc-950/50">
        <Section className="!py-12 md:!py-16">
          <div className="grid grid-cols-3 gap-4 md:gap-8 text-center">
            {[
              { end: 539, suffix: '+', label: 'Alunos atendidos' },
              { end: 99, suffix: '%', label: 'Satisfação' },
              { end: 20, suffix: ' anos', label: 'De experiência' },
            ].map((s, i) => (
              <div key={s.label}>
                <p className="font-primary font-black text-3xl md:text-5xl" style={{ color: gold }}>
                  <AnimatedCounter end={s.end} suffix={s.suffix} />
                </p>
                <p className="text-zinc-500 text-xs md:text-sm uppercase tracking-widest mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </Section>
      </div>

      {/* WALL OF FAME - Marquee de fotos de alunos */}
      {gallery.length > 0 && (
        <div className="border-b border-white/10 py-14 md:py-20 bg-black overflow-hidden">
          <div className="max-w-6xl mx-auto px-6 md:px-10 text-center mb-8">
            <SectionKicker>Wall of Fame</SectionKicker>
            <H2>Alunos que <span style={{ color: gold }}>evoluíram</span> com a gente</H2>
            <p className="text-zinc-400 max-w-2xl mx-auto">Rostos reais, resultados reais. Nossa comunidade em movimento.</p>
          </div>

          <style>{`
            @keyframes ferrazMarquee {
              from { transform: translateX(0); }
              to { transform: translateX(-50%); }
            }
            .ferraz-marquee-track {
              display: flex;
              gap: 1rem;
              width: max-content;
              animation: ferrazMarquee ${Math.max(30, gallery.length * 4)}s linear infinite;
            }
            .ferraz-marquee-track:hover { animation-play-state: paused; }
            @media (prefers-reduced-motion: reduce) {
              .ferraz-marquee-track { animation: none; }
            }
          `}</style>

          <div className="relative">
            {/* Fade edges */}
            <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-black to-transparent z-10" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-black to-transparent z-10" />

            <div className="ferraz-marquee-track">
              {[...gallery, ...gallery].map((photo, idx) => (
                <div
                  key={`${photo.id}-${idx}`}
                  className="flex-shrink-0 w-40 h-56 md:w-52 md:h-72 rounded-2xl overflow-hidden border border-white/10 relative"
                >
                  <img
                    src={photo.url.startsWith('http') ? photo.url : `${process.env.REACT_APP_BACKEND_URL}${photo.url}`}
                    alt="Aluno Ferraz Team"
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* THIAGO FERRAZ */}
      <div className="bg-gradient-to-b from-zinc-950/50 to-black border-y border-white/10">
        <Section>
          <div className="grid md:grid-cols-5 gap-10 items-center">
            <div className="md:col-span-2 relative aspect-[3/4] rounded-2xl overflow-hidden border border-white/10">
              <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url('${thiagoUrl}')` }} />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <p className="text-[10px] uppercase tracking-widest" style={{ color: gold }}>Head Coach</p>
                <p className="font-primary font-black text-2xl">Thiago Ferraz</p>
              </div>
            </div>
            <div className="md:col-span-3">
              <SectionKicker>Autoridade</SectionKicker>
              <H2>Nada de treino <span style={{ color: gold }}>genérico.</span></H2>
              <p className="text-zinc-400 leading-relaxed mb-6">
                Cada treino é desenvolvido pelo Thiago Ferraz de forma <span className="text-white font-bold">100% personalizada</span>, considerando seu objetivo, experiência, disponibilidade, limitações e equipamentos disponíveis.
              </p>
              <div className="space-y-3">
                {['Objetivo definido', 'Nível de experiência', 'Sua rotina real', 'Limitações e lesões', 'Equipamentos disponíveis'].map((it) => (
                  <div key={it} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: gold }}>
                      <Check className="w-3.5 h-3.5 text-black" strokeWidth={3} />
                    </div>
                    <span className="text-zinc-300">{it}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </Section>
      </div>

      {/* GAMIFICATION */}
      <div className="border-y border-white/10 bg-gradient-to-b from-black via-zinc-950/30 to-black">
        <Section>
          <div className="text-center mb-12">
            <SectionKicker>Diferencial exclusivo</SectionKicker>
            <H2><span style={{ color: gold }}>Gamificação</span> que te mantém consistente</H2>
            <p className="text-zinc-400 max-w-2xl mx-auto">Cada treino conta. Cada dia importa. Transforme a sua evolução em recompensas reais.</p>
          </div>
          <div className="grid md:grid-cols-4 gap-5">
            {[
              { icon: Dumbbell, label: 'Treine', desc: 'Faça seus treinos e complete metas.' },
              { icon: Star, label: 'Ganhe pontos', desc: 'Pontos por cada treino, meta e interação.' },
              { icon: Coins, label: 'Acumule moedas', desc: 'Pontos viram moedas Ferraz automaticamente.' },
              { icon: Trophy, label: 'Recompensas', desc: 'Troque por produtos exclusivos da loja.' },
            ].map((s, i) => (
              <motion.div key={s.label} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="relative">
                <div className="rounded-2xl p-6 bg-white/[0.03] border border-white/10 text-center hover:border-[#D4AF37]/40 transition-all h-full">
                  <div className="w-14 h-14 mx-auto mb-4 rounded-2xl flex items-center justify-center" style={{ background: `linear-gradient(135deg, ${gold}, #8B6914)` }}>
                    <s.icon className="w-6 h-6 text-black" />
                  </div>
                  <p className="font-bold text-white mb-2">{s.label}</p>
                  <p className="text-zinc-400 text-xs leading-relaxed">{s.desc}</p>
                </div>
                {i < 3 && <ArrowRight className="hidden md:block absolute top-1/2 -right-3 w-5 h-5 z-10" style={{ color: gold }} />}
              </motion.div>
            ))}
          </div>
        </Section>
      </div>

      {/* COMMUNITY */}
      <Section>
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div className="relative">
            <div className="aspect-square rounded-2xl overflow-hidden border border-white/10">
              <div className="w-full h-full bg-cover bg-center" style={{ backgroundImage: `url('${communityUrl}')` }} />
            </div>
            <div className="absolute -bottom-6 -right-6 hidden md:block px-5 py-3 rounded-2xl bg-black border border-[#D4AF37]/30 shadow-2xl">
              <div className="flex items-center gap-2">
                <Heart className="w-4 h-4" style={{ color: gold }} />
                <span className="text-white text-sm font-bold">+2.4k interações/mês</span>
              </div>
            </div>
          </div>
          <div>
            <SectionKicker>Comunidade exclusiva</SectionKicker>
            <H2>Ninguém evolui <span style={{ color: gold }}>sozinho.</span></H2>
            <p className="text-zinc-400 leading-relaxed mb-6">
              Compartilhe resultados, motive outros alunos, receba incentivo e crie amizades reais dentro da comunidade Ferraz Team.
            </p>
            <div className="space-y-3">
              {[
                { icon: TrendingUp, txt: 'Compartilhe sua evolução' },
                { icon: MessageCircle, txt: 'Troque experiências' },
                { icon: Heart, txt: 'Receba incentivo diário' },
                { icon: Users, txt: 'Crie amizades e conexões' },
              ].map((i, k) => (
                <div key={i.txt} className="flex items-center gap-3">
                  <i.icon className="w-4 h-4" style={{ color: gold }} />
                  <span className="text-zinc-300 text-sm">{i.txt}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* FEATURES */}
      <div className="border-y border-white/10 bg-gradient-to-b from-zinc-950/50 to-black">
        <Section>
          <div className="text-center mb-12">
            <SectionKicker>Tecnologia da plataforma</SectionKicker>
            <H2>Tudo o que você precisa,<br /><span style={{ color: gold }}>em um só lugar.</span></H2>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { icon: Dumbbell, title: 'Treino personalizado', desc: 'Plano montado especialmente para você — atualizado conforme sua evolução.' },
              { icon: Video, title: 'Biblioteca em vídeo', desc: 'Cada exercício com vídeo profissional de execução, sem dúvidas.' },
              { icon: TrendingUp, title: 'Registro de carga', desc: 'Anote peso e reps de cada sessão. Gráfico de evolução automático.' },
              { icon: Timer, title: 'Cronômetro inteligente', desc: 'Timer de descanso entre séries com alertas visuais e sonoros.' },
              { icon: Calendar, title: 'Calendário completo', desc: 'Veja todos os dias treinados e acompanhe seu histórico visualmente.' },
              { icon: Camera, title: 'Fotos de evolução', desc: 'Registre suas transformações mês a mês diretamente na plataforma.' },
              { icon: Trophy, title: 'Gamificação', desc: 'Pontos, moedas, recompensas e ranking para te manter motivado.' },
              { icon: Users, title: 'Comunidade', desc: 'Feed exclusivo com outros alunos. Interações e apoio real.' },
              { icon: Zap, title: 'Acompanhamento pro', desc: 'Feedback profissional direto na plataforma, sempre que precisar.' },
            ].map((f, i) => <FeatureCard key={f.title} {...f} />)}
          </div>
        </Section>
      </div>

      {/* HOW IT WORKS */}
      <Section>
        <div className="text-center mb-12">
          <SectionKicker>Como funciona</SectionKicker>
          <H2>Comece em <span style={{ color: gold }}>4 passos.</span></H2>
        </div>
        <div className="grid md:grid-cols-4 gap-5">
          {[
            'Escolha seu plano',
            'Preencha sua avaliação',
            'Receba seu treino personalizado',
            'Comece sua evolução',
          ].map((step, i) => (
            <motion.div key={step} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
              className="relative rounded-2xl p-6 bg-white/[0.03] border border-white/10">
              <div className="font-primary font-black text-6xl leading-none mb-3 opacity-30" style={{ color: gold }}>{i + 1}</div>
              <p className="text-white font-bold text-base">{step}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* BONUS */}
      <div className="border-y border-white/10 bg-gradient-to-b from-black via-zinc-950/60 to-black relative overflow-hidden">
        {/* Golden glow orbs */}
        <div className="absolute -top-32 left-1/4 w-96 h-96 rounded-full opacity-[0.08] blur-3xl pointer-events-none" style={{ background: gold }} />
        <div className="absolute -bottom-32 right-1/4 w-96 h-96 rounded-full opacity-[0.08] blur-3xl pointer-events-none" style={{ background: gold }} />

        <Section>
          <div className="text-center mb-14 relative">
            {/* Golden badge with icon */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ type: 'spring', stiffness: 200 }}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6 border shadow-[0_8px_30px_rgba(212,175,55,0.25)]"
              style={{ borderColor: gold, background: 'linear-gradient(135deg, rgba(212,175,55,0.15), rgba(212,175,55,0.05))' }}
            >
              <Gift className="w-4 h-4" style={{ color: gold }} />
              <span className="text-[10px] uppercase tracking-[0.3em] font-black" style={{ color: gold }}>Presentes exclusivos</span>
              <Sparkles className="w-4 h-4" style={{ color: gold }} />
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-primary font-black text-4xl md:text-6xl leading-[1.02] mb-4"
            >
              Ganhe <span className="relative inline-block">
                <span className="bg-gradient-to-b from-[#F5E28A] via-[#D4AF37] to-[#8B6914] bg-clip-text text-transparent">2 bônus</span>
                <span className="absolute -bottom-1 left-0 right-0 h-1 rounded-full" style={{ background: `linear-gradient(90deg, transparent, ${gold}, transparent)` }} />
              </span>
              <br />ao entrar hoje.
            </motion.h2>

            <p className="text-zinc-400 text-base md:text-lg max-w-xl mx-auto">
              Programas completos que <span className="text-white font-bold">valem sozinhos o investimento</span> — inclusos gratuitamente na sua assinatura.
            </p>

            <div className="flex items-center justify-center gap-2 mt-6 text-xs md:text-sm">
              <span className="px-3 py-1.5 rounded-full text-black font-black uppercase tracking-widest" style={{ background: gold }}>Grátis</span>
              <span className="text-zinc-500 line-through">R$ 197</span>
              <ArrowRight className="w-3.5 h-3.5 text-zinc-500" />
              <span className="text-white font-bold">R$ 0 hoje</span>
            </div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {[
              {
                title: '168 dias de corrida',
                desc: 'Planilha completa para melhorar condicionamento cardiovascular do zero ao 10km.',
                tag: 'BÔNUS 01',
                img: 'https://customer-assets-wrfwihn1.emergentagent.net/job_c86a48b9-ffb6-4978-b66b-b68da722b4fb/artifacts/z9v2njjk_71WGBFo9xcL._SL1500_%20%281%29.jpg',
              },
              {
                title: '10 treinos com peso do corpo',
                desc: 'Programa completo para treinar em casa, viagens ou quando não puder ir à academia.',
                tag: 'BÔNUS 02',
                img: 'https://customer-assets-wrfwihn1.emergentagent.net/job_c86a48b9-ffb6-4978-b66b-b68da722b4fb/artifacts/zg3mp669_capahotmart.jpg',
              },
            ].map((b, i) => (
              <motion.div key={b.tag} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="relative rounded-2xl overflow-hidden border flex flex-col" style={{ borderColor: '#D4AF37', background: 'linear-gradient(135deg, rgba(212,175,55,0.06), rgba(0,0,0,0.4))' }}>
                <div className="relative aspect-[4/3] overflow-hidden bg-black">
                  <img src={b.img} alt={b.title} loading="lazy" className="w-full h-full object-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                  <span className="absolute top-4 left-4 text-[10px] uppercase tracking-widest font-black px-2.5 py-1 rounded-full" style={{ background: gold, color: '#000' }}>{b.tag}</span>
                </div>
                <div className="relative p-6 md:p-8">
                  <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full opacity-10" style={{ background: gold }} />
                  <h3 className="font-primary font-black text-2xl md:text-3xl mb-3">{b.title}</h3>
                  <p className="text-zinc-400">{b.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </Section>
      </div>

      {/* OFFER */}
      <Section>
        <motion.div initial={{ opacity: 0, scale: 0.98 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
          className="relative rounded-3xl overflow-hidden border-2 p-8 md:p-14 text-center" style={{ borderColor: gold, background: 'radial-gradient(circle at center, rgba(212,175,55,0.15) 0%, rgba(0,0,0,0.4) 60%)' }}>
          <div className="absolute top-6 right-6 md:top-8 md:right-8 flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-black uppercase tracking-widest" style={{ background: gold, color: '#000' }}>
            <Sparkles className="w-3.5 h-3.5" /> Oferta especial
          </div>
          <SectionKicker>Sua vaga na Ferraz Team</SectionKicker>
          <H2>Menos que o preço de<br /><span style={{ color: gold }}>uma pizza por mês.</span></H2>
          <div className="my-8">
            <p className="text-zinc-500 line-through text-xl mb-1">De R$ 150,00/mês</p>
            <p className="font-primary text-6xl md:text-8xl font-black leading-none" style={{ color: gold }}>
              R$ 68<span className="text-4xl md:text-5xl">,99</span>
            </p>
            <p className="text-zinc-400 text-sm mt-2">por mês</p>
          </div>
          <div className="flex justify-center mb-6">
            <CTAButton href={CTA_HREF} {...ctaProps} className="!px-12 !py-6 !text-base md:!text-lg" data-testid="cta-offer">Quero garantir minha vaga</CTAButton>
          </div>
          {scarcity.enabled && (
            <div className="flex justify-center mb-6">
              <ScarcityBar scarcity={scarcity} />
            </div>
          )}
          <div className="flex flex-wrap items-center justify-center gap-3">
            <TrustPill icon={ShieldCheck} text="Pagamento seguro" />
            <TrustPill icon={Lock} text="Dados protegidos" />
            <TrustPill icon={Check} text="Cancele quando quiser" />
          </div>
        </motion.div>
      </Section>

      {/* GUARANTEE */}
      <div className="border-y border-white/10">
        <Section>
          <div className="max-w-3xl mx-auto text-center">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center border-2" style={{ borderColor: gold, background: 'rgba(212,175,55,0.1)' }}>
              <ShieldCheck className="w-10 h-10" style={{ color: gold }} />
            </div>
            <SectionKicker>Risco zero para você</SectionKicker>
            <H2>Garantia de <span style={{ color: gold }}>7 dias.</span></H2>
            <p className="text-zinc-400 leading-relaxed mb-8 text-base md:text-lg">
              Você pode usar toda a plataforma durante 7 dias. Se entender que ela não é para você, devolvemos <span className="text-white font-bold">100% do seu dinheiro</span>. Sem burocracia. Sem perguntas desnecessárias. <br /><br />
              <span className="font-bold text-white">Quem assume o risco somos nós. Não você.</span>
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              <TrustPill icon={Check} text="Garantia de 7 dias" />
              <TrustPill icon={ShieldCheck} text="Compra segura" />
              <TrustPill icon={Award} text="Reembolso integral" />
            </div>
          </div>
        </Section>
      </div>

      {/* FAQ */}
      <Section>
        <div className="text-center mb-10">
          <SectionKicker>Perguntas frequentes</SectionKicker>
          <H2>Ainda com dúvidas?</H2>
        </div>
        <div className="max-w-3xl mx-auto">
          {[
            { q: 'Funciona para iniciantes?', a: 'Sim! O plano é ajustado ao seu nível. Iniciantes recebem uma progressão gradual, segura e específica.' },
            { q: 'Posso treinar em casa?', a: 'Claro. Basta informar seus equipamentos na avaliação — o treino é adaptado à sua realidade.' },
            { q: 'Posso treinar em academia?', a: 'Sim. Você define onde treina e o plano é montado com base nos aparelhos disponíveis.' },
            { q: 'Como funciona o acompanhamento?', a: 'Você tem canal direto com o Thiago dentro da plataforma. Feedbacks, ajustes e suporte sempre que precisar.' },
            { q: 'Como funciona a garantia?', a: '7 dias corridos após a assinatura. Se não gostar, basta pedir reembolso — devolvemos 100%.' },
            { q: 'Como funciona a gamificação?', a: 'Cada treino, meta cumprida e interação gera pontos. Pontos viram moedas. Moedas são trocadas por produtos na loja Ferraz.' },
            { q: 'Como ganho moedas?', a: 'Treinando, completando metas mensais, interagindo na comunidade e sendo consistente na plataforma.' },
            { q: 'Como recebo meu treino?', a: 'Direto na plataforma exclusiva, com vídeos de execução, cronômetro, registro de carga e todo o suporte necessário.' },
          ].map((f, i) => <FAQItem key={f.q} testId={`faq-item-${i}`} {...f} />)}
        </div>
      </Section>

      {/* FINAL CTA */}
      <div className="border-t border-white/10 bg-gradient-to-b from-black via-zinc-950 to-black">
        <Section className="text-center">
          <SectionKicker>Sua vez</SectionKicker>
          <H2>Sua transformação<br /><span style={{ color: gold }}>começa hoje.</span></H2>
          <p className="text-zinc-400 max-w-2xl mx-auto mb-10 text-base md:text-lg leading-relaxed">
            Entre para a Ferraz Team e tenha acesso a uma plataforma completa, com treino personalizado, tecnologia, gamificação, comunidade e acompanhamento profissional para acelerar seus resultados.
          </p>
          <CTAButton href={CTA_HREF} {...ctaProps} className="!px-12 !py-6 !text-base md:!text-lg" data-testid="cta-final">Quero fazer parte da Ferraz Team</CTAButton>
        </Section>
      </div>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 px-6 text-center">
        <p className="text-zinc-600 text-xs">© {new Date().getFullYear()} Ferraz Team. Todos os direitos reservados.</p>
      </footer>
    </div>
  );
};

export default LandingPage;
